import { useState } from 'react';
import UserAvatar from '../../shared/user-avatar';
import { dayjsWithExtensions as dayjs } from '../../utils/dayjs-config';
import RecentAdminActionsTrend from './recent-admin-actions-trend';
import TileInsufficientAccessNotice from './tile-insufficient-access-notice';
import type { CommandDashboardTileBodyProps } from './tile-content-registry';
import { useCanSeeTile } from './use-tile-access';
import {
  RECENT_ADMIN_ACTIONS_PAGE_SIZE,
  useRecentAdminActionsTile,
} from './use-recent-admin-actions-tile';
import {
  useBindTileRefresh,
  useTrackCommandDashboardTileRefresh,
} from './use-tile-refresh';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';

export const RECENT_ADMIN_ACTIONS_TWO_THIRDS_WIDTH_MAX = 1074;

const TWO_THIRDS_WIDTH_CONTAINER_QUERY = `@container (max-width: ${RECENT_ADMIN_ACTIONS_TWO_THIRDS_WIDTH_MAX}px)`;
const ROW_SX = {
  height: 72,
  [TWO_THIRDS_WIDTH_CONTAINER_QUERY]: { height: 84 },
};

export default function RecentAdminActionsTileContent({
  tile,
}: CommandDashboardTileBodyProps) {
  const hasPermission = useCanSeeTile(tile);

  const {
    entries,
    totalCount,
    todayCount,
    yesterdayCount,
    hasLoadedData,
    isLoading,
    isFetching,
    refetch,
    page,
    onPageChange,
    sortDirection,
    toggleSort,
  } = useRecentAdminActionsTile(hasPermission);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const trackRefresh = useTrackCommandDashboardTileRefresh();
  const onRefresh = async () => {
    setIsRefreshing(true);
    try {
      const result = await refetch();
      if (!result.isError) {
        trackRefresh();
      }
    } finally {
      setIsRefreshing(false);
    }
  };
  useBindTileRefresh(tile, hasPermission, { onRefresh, isFetching });

  const showSkeleton = isLoading || isRefreshing;

  if (!hasPermission && !hasLoadedData) {
    return <TileInsufficientAccessNotice tile={tile} />;
  }

  return (
    <Box
      data-testid="recent-admin-actions-tile"
      sx={{
        containerType: 'inline-size',
        flex: tile.sizeProfile === 'wide' ? 1 : undefined,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: tile.sizeProfile === 'wide' ? 1 : undefined,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          pb: 3,
          [TWO_THIRDS_WIDTH_CONTAINER_QUERY]: { pb: 0 },
        }}
      >
        <Stack
          sx={{
            px: 3,
            py: 1.5,
            [TWO_THIRDS_WIDTH_CONTAINER_QUERY]: { py: 1.25 },
            width: 540,
            maxWidth: '100%',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            divider={
              <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
            }
            sx={{ height: 64, width: '100%' }}
          >
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              sx={{ px: 1, py: 2 }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  lineHeight: 1.5,
                  letterSpacing: '0.15px',
                  color: (theme) => theme.palette.semantic.text.title2,
                }}
              >
                Today&apos;s Actions
              </Typography>
              <Typography
                sx={{
                  fontSize: '24px',
                  lineHeight: 1.334,
                  letterSpacing: 0,
                  color: (theme) => theme.palette.semantic.stepper.active,
                }}
              >
                {todayCount}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              sx={{ px: 1, py: 2 }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  lineHeight: 1.5,
                  letterSpacing: '0.15px',
                  color: (theme) => theme.palette.semantic.text.title2,
                }}
              >
                Yesterday
              </Typography>
              <Typography
                sx={{
                  fontSize: '24px',
                  lineHeight: 1.334,
                  letterSpacing: 0,
                  color: (theme) => theme.palette.semantic.stepper.active,
                }}
              >
                {yesterdayCount}
              </Typography>
            </Stack>
            <Stack sx={{ px: 1.5, py: 1 }}>
              <RecentAdminActionsTrend
                todayCount={todayCount}
                yesterdayCount={yesterdayCount}
              />
            </Stack>
          </Stack>
        </Stack>

        <TableContainer sx={{ px: 3, flexShrink: 0 }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  height: 56,
                  '& .MuiTableCell-root': {
                    position: 'relative',
                    bgcolor: (theme) => theme.palette.semantic.surface.bg3,
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: '24px',
                      width: '1px',
                      backgroundColor: (theme) => theme.palette.divider,
                    },
                    '&:first-of-type': { borderTopLeftRadius: '4px' },
                    '&:last-of-type': { borderTopRightRadius: '4px' },
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '14px',
                    letterSpacing: '0.17px',
                    lineHeight: '24px',
                    color: (theme) => theme.palette.semantic.text.title2,
                    whiteSpace: 'nowrap',
                    width: 240,
                    pr: 0.5,
                  }}
                >
                  <TableSortLabel
                    active={sortDirection !== undefined}
                    direction={sortDirection === 'desc' ? 'desc' : 'asc'}
                    sx={{
                      fontWeight: 500,
                      color: 'inherit',
                      '&:hover, &:focus, &.Mui-active': { color: 'inherit' },
                      '& .MuiTableSortLabel-icon': {
                        opacity: 1,
                        color: 'inherit',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSort();
                    }}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '14px',
                    letterSpacing: '0.17px',
                    lineHeight: '24px',
                    color: (theme) => theme.palette.semantic.text.title2,
                    whiteSpace: 'nowrap',
                    pr: 0.5,
                  }}
                >
                  Action Description
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '14px',
                    letterSpacing: '0.17px',
                    lineHeight: '24px',
                    color: (theme) => theme.palette.semantic.text.title2,
                    whiteSpace: 'nowrap',
                    width: 240,
                    pr: 0.5,
                  }}
                >
                  Date and Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showSkeleton &&
                Array.from({ length: RECENT_ADMIN_ACTIONS_PAGE_SIZE }).map(
                  (_, index) => (
                    // eslint-disable-next-line react-x/no-array-index-key
                    <TableRow key={index} sx={ROW_SX}>
                      <TableCell>
                        <Skeleton width={140} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={280} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={100} />
                      </TableCell>
                    </TableRow>
                  )
                )}
              {!showSkeleton && entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2, textAlign: 'center' }}
                    >
                      No recent admin actions.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!showSkeleton &&
                entries.map((entry, index) => (
                  <TableRow
                    // eslint-disable-next-line react-x/no-array-index-key
                    key={`${entry.timestamp}-${index}`}
                    sx={ROW_SX}
                  >
                    <TableCell sx={{ pl: 3 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <UserAvatar
                          userEmail={entry.adminName}
                          sx={{ width: 32, height: 32 }}
                        />
                        <Typography variant="body2">
                          {entry.adminName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ pl: 3 }}>
                      <Typography variant="body2">{entry.action}</Typography>
                    </TableCell>
                    <TableCell sx={{ pl: 3, whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {dayjs(entry.timestamp).fromNow()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Absorbs whatever height the card has left over above its fixed total,
          the same way Figma's own "Auto" gap does - zero when the table
          already fills the space, whatever's left otherwise. */}
        <Box sx={{ flex: 1 }} />

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={RECENT_ADMIN_ACTIONS_PAGE_SIZE}
          rowsPerPageOptions={[]}
          sx={{
            flexShrink: 0,
            [TWO_THIRDS_WIDTH_CONTAINER_QUERY]: {
              borderTop: (theme) =>
                theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.23)'
                  : '1px solid rgba(0, 0, 0, 0.23)',
            },
            '& .MuiTablePagination-toolbar': { minHeight: 52 },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '12px',
              lineHeight: 1.66,
              letterSpacing: '0.4px',
              color: 'text.primary',
            },
            '& .MuiTablePagination-actions': { ml: '26px' },
          }}
        />
      </Box>
    </Box>
  );
}
